import { useEffect, useMemo, useState } from 'react';

import {
  extractCodeEvaluatorFeedbackKeys,
  extractEvaluatorFeedbackKeys,
} from '@/components/Rules/RulesEvaluatorPane.utils';
import { useRunRules } from '@/hooks/useSwr';
import { RuleSchema, StructuredEvaluatorSchema, XOR } from '@/types/schema';

export const useEvaluatorFeedbackKeys = ({
  organizationId,
  datasetId,
  sessionId,
  selectedRuleIds,
  excludedRuleIds,
}: {
  organizationId: string | null;
  datasetId?: string | null;
  sessionId?: string | null;
} & XOR<
  { selectedRuleIds?: Set<string> },
  { excludedRuleIds?: Set<string> }
>) => {
  const { data: rules } = useRunRules(
    datasetId
      ? { dataset_id: datasetId }
      : sessionId
      ? { session_id: sessionId }
      : null
  );

  const rulesToUse = useMemo(() => {
    if (!rules) return rules;
    if (selectedRuleIds) {
      return rules.filter((rule) => selectedRuleIds.has(rule.id));
    }
    if (excludedRuleIds) {
      return rules.filter((rule) => !excludedRuleIds.has(rule.id));
    }
    return rules;
  }, [rules, selectedRuleIds, excludedRuleIds]);

  return useEvaluatorFeedbackKeysFromRules({
    rules: !datasetId && !sessionId ? [] : rulesToUse,
    organizationId,
  });
};

export const useEvaluatorFeedbackKeysFromRules = ({
  rules,
  organizationId,
}: {
  rules?: RuleSchema[];
  organizationId: string | null;
}) => {
  const [feedbackKeys, setFeedbackKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // get pending evaluator feedback keys
  useEffect(() => {
    const fetchEvaluatorFeedbackKeys = async () => {
      setIsLoading(true);
      try {
        const codeEvaluatorMappings = rules?.map((i) => [
          i.code_evaluators ? i.code_evaluators[0] : undefined,
          i.display_name,
          i.id,
        ]);

        const feedbackKeys = (
          await Promise.all(
            rules?.map((rule) => {
              // Handle evaluators
              if (rule.evaluators?.length && organizationId) {
                return Promise.all(
                  rule.evaluators
                    .map((i) => i?.structured)
                    .filter((x): x is StructuredEvaluatorSchema => x != null)
                    .map((schema) =>
                      extractEvaluatorFeedbackKeys(organizationId, schema)
                    )
                );
              }
              // Handle code evaluators
              else if (rule.code_evaluators?.length) {
                const codeMapping = codeEvaluatorMappings?.find(
                  (mapping) => mapping[2] === rule.id
                );
                if (codeMapping && codeMapping[0]) {
                  const codeEvaluator = codeMapping[0] as {
                    code: string;
                    language?: 'python' | 'javascript';
                  };
                  return extractCodeEvaluatorFeedbackKeys(
                    codeEvaluator.code,
                    rule.display_name,
                    codeEvaluator.language || 'python'
                  );
                }
              }
              return [];
            }) || []
          )
        ).flat(2); // Flatten nested arrays

        if (!rules?.length) {
          setFeedbackKeys([]);
        } else {
          setFeedbackKeys(feedbackKeys);
        }
      } catch (error) {
        console.error('Error fetching evaluator feedback keys:', error);
        setFeedbackKeys([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvaluatorFeedbackKeys();
  }, [organizationId, rules]);

  return {
    evaluatorFeedbackKeys: feedbackKeys,
    isLoading,
  };
};
